import {
  Box,
  Badge,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { WarningAmberRounded, MoreHoriz, MoreVert } from "@mui/icons-material";
import axios from "axios";
import DeleteModal from "components/Modals/DeleteModal";
import { deleteData } from "const/axios";
import { API_URL } from "const/constants";
import { P } from "const/permissions";
import useKothar from "context/useKothar";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useMemo, useEffect, useState, useRef } from "react";
import { FaMoneyBillWave, FaPlusCircle } from "react-icons/fa";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { RefreshData } from "components/helper";
import { AiFillDelete, AiFillEdit } from "react-icons/ai";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { hasPermission } from "utils/permissionUtils";
import { formatMoney } from "utils/utils";
import DisplayDate from "components/DisplayDate";
import { formatDisplayDate } from "utils/dateFormat";
import { useReactToPrint } from "react-to-print";
import InvoiceTemplate from "./InvoiceTemplate";
import InvoiceVerificationRequests from "./InvoiceVerificationRequests";
import InvoiceActivationRequests from "./InvoiceActivationRequests";
import PurchaseOrderTemplate from "./PurchaseOrderTemplate";
import { BsEyeFill } from "react-icons/bs";
import { alpha } from "@mui/material/styles";
import InvoicePrintThemeToggle from "components/Invoice/InvoicePrintThemeToggle";
import { useInvoicePrintTheme } from "hooks/useInvoicePrintTheme";
import { getInvoicePrintPageStyle } from "utils/invoicePrintTheme";
import { refreshPendingVerificationQueries } from "utils/invoiceVerificationQueries";
import { getInvoiceNumber } from "utils/invoiceDashboardUtils";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import SendInvoiceEmailDialog from "components/Invoice/SendInvoiceEmailDialog";
import ModuleInvoiceSourceChip from "components/ModuleInvoice/ModuleInvoiceSourceChip";
import { isModuleInvoice } from "components/ModuleInvoice/moduleLinks";

const INVOICE_SUMMARY_TONES = {
  neutral: {
    container: "border-app-border bg-app-surface/70",
    value: "text-app-text",
  },
  paid: {
    container: "border-emerald-500/30 bg-emerald-500/10",
    value: "text-emerald-700 dark:text-emerald-300",
  },
  partial: {
    container: "border-amber-500/30 bg-amber-500/10",
    value: "text-amber-700 dark:text-amber-300",
  },
  unpaid: {
    container: "border-rose-500/30 bg-rose-500/10",
    value: "text-rose-700 dark:text-rose-300",
  },
};

const PAYMENT_STATUS = {
  UNPAID: "UNPAID",
  PARTIALLY_PAID: "PARTIALLY_PAID",
  PAID: "PAID",
};

const FILTER_STATUS = {
  DRAFT: "DRAFT",
  PENDING: "PENDING",
};

const getPaymentStatus = (invoice) =>
  invoice?.paymentStatus ||
  (invoice?.status === true ? PAYMENT_STATUS.PAID : PAYMENT_STATUS.UNPAID);

const paymentStatusLabel = (status) =>
  status === PAYMENT_STATUS.PAID
    ? "Paid"
    : status === PAYMENT_STATUS.PARTIALLY_PAID
    ? "Partially Paid"
    : "Unpaid";

// Component to handle printing for each invoice
const InvoicePrintHandler = ({ item, onPrintInvoice, onPrintPurchaseOrder }) => {
  const { darkPrint } = useInvoicePrintTheme();
  const invoiceRef = useRef(null);
  const purchaseOrderRef = useRef(null);
  const sanitizedInvoiceTitle =
    item?.title
      ?.toString()
      ?.trim()
      ?.replace(/[\\/:*?"<>|]+/g, "-")
      ?.replace(/\s+/g, " ")
      ?.slice(0, 120) || `Invoice-${item?.invoiceId || "download"}`;

  const sanitizedPurchaseOrderTitle =
    item?.title
      ?.toString()
      ?.trim()
      ?.replace(/[\\/:*?"<>|]+/g, "-")
      ?.replace(/\s+/g, " ")
      ?.slice(0, 120) || `Purchase-Order-${item?.invoiceId || "download"}`;

  const handlePrintInvoice = useReactToPrint({
    contentRef: invoiceRef,
    documentTitle: sanitizedInvoiceTitle,
    pageStyle: getInvoicePrintPageStyle(darkPrint),
  });

  const handlePrintPurchaseOrder = useReactToPrint({
    contentRef: purchaseOrderRef,
    documentTitle: sanitizedPurchaseOrderTitle,
    pageStyle: getInvoicePrintPageStyle(darkPrint),
  });

  // Expose print functions to parent
  useEffect(() => {
    if (onPrintInvoice) {
      onPrintInvoice.current = handlePrintInvoice;
    }
    if (onPrintPurchaseOrder) {
      onPrintPurchaseOrder.current = handlePrintPurchaseOrder;
    }
  }, [
    darkPrint,
    handlePrintInvoice,
    handlePrintPurchaseOrder,
    onPrintInvoice,
    onPrintPurchaseOrder,
  ]);

  return (
    <div
      style={{
        position: "absolute",
        left: "-9999px",
        top: "-9999px",
      }}
    >
      <div ref={invoiceRef}>
        <InvoiceTemplate printData={item} />
      </div>
      <div ref={purchaseOrderRef}>
        <PurchaseOrderTemplate printData={item} />
      </div>
    </div>
  );
};

const getApiErrorMessage = (error) =>
  error?.response?.data?.message || error?.response?.data?.errorMessage || error?.message || "";

/** Compact overflow menu for invoice row actions on small screens. */
const InvoiceRowActionsMenu = ({
  item,
  canEmailInvoices,
  onPrintPurchaseOrder,
  onPrintInvoice,
  onEmail,
  onOpen,
  onDelete,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const close = () => setAnchorEl(null);
  const showPo = getPaymentStatus(item) !== PAYMENT_STATUS.PAID;
  const linked = isModuleInvoice(item);
  const viewOnly = Boolean(item?.status) || linked;

  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
        aria-label="Invoice actions"
      >
        <MoreVert fontSize="small" className="text-app-text-secondary" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        onClick={(e) => e.stopPropagation()}
      >
        {showPo && (
          <MenuItem
            onClick={() => {
              close();
              onPrintPurchaseOrder?.(item);
            }}
          >
            <ListItemIcon>
              <span className="text-xs font-bold text-primary">PO</span>
            </ListItemIcon>
            <ListItemText>Print purchase order</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            close();
            onPrintInvoice?.(item);
          }}
        >
          <ListItemIcon>
            <SaveAltIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download invoice</ListItemText>
        </MenuItem>
        {canEmailInvoices && item?.lifecycleStatus === "ACTIVE" && (
          <MenuItem
            onClick={() => {
              close();
              onEmail?.(item);
            }}
          >
            <ListItemIcon>
              <EmailOutlinedIcon fontSize="small" className="text-app-primary" />
            </ListItemIcon>
            <ListItemText>Email invoice</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            close();
            onOpen?.(item);
          }}
        >
          <ListItemIcon>
            {viewOnly ? (
              <BsEyeFill className="text-blue-500" />
            ) : (
              <AiFillEdit className="text-app-text-secondary" />
            )}
          </ListItemIcon>
          <ListItemText>{viewOnly ? "View invoice" : "Edit invoice"}</ListItemText>
        </MenuItem>
        {!linked && (
          <MenuItem
            onClick={() => {
              close();
              onDelete?.(item);
            }}
          >
            <ListItemIcon>
              <AiFillDelete className="text-red-600" />
            </ListItemIcon>
            <ListItemText>Delete invoice</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

const Invoice = () => {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("lg"));
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { state: locationState } = useLocation();
  const [openConfirmationModal, setOpenConfirmationModal] = useState({});
  const [selectedItems, setSelectedItems] = useState({}); // { [invoiceId]: Set<itemId> }
  const [markUnpaidConfirm, setMarkUnpaidConfirm] = useState(null); // { itemId, invoiceId, description, componentType }
  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);
  const [activationDialogOpen, setActivationDialogOpen] = useState(false);
  const [emailInvoice, setEmailInvoice] = useState(null);
  const [headerMoreAnchor, setHeaderMoreAnchor] = useState(null);
  const canVerifyInvoices = hasPermission(P.INVOICE.VERIFY);
  const canEmailInvoices = hasPermission(P.INVOICE.SEND);
  const { data: pendingVerifications = [] } = useQuery({
    queryKey: ["invoice-verification-pending-count"],
    queryFn: () =>
      axios.get(`${API_URL}/invoice-verification/pending`).then((r) => r.data?.data ?? []),
    enabled: canVerifyInvoices,
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
  });
  const pendingVerificationCount = pendingVerifications.length;
  // Only the total is wanted here, so ask for the smallest page the endpoint will return.
  const { data: pendingActivationCount = 0 } = useQuery({
    queryKey: ["invoice-activation-pending-count"],
    queryFn: () =>
      axios
        .get(`${API_URL}/invoice-verification/activations/pending`, {
          params: { page: 0, size: 1 },
        })
        .then((r) => r.data?.data?.totalItems ?? 0),
    enabled: canVerifyInvoices,
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
  });
  const [
    { invoiceList, token, isInvoiceListLoading, invoicePagination, invoiceSummary },
    { refetchInvoiceList, setInvoicePagination, refetchInvoiceSummary, fetchInvoiceList },
  ] = useKothar();

  const initialLoadDoneRef = useRef(false);
  useEffect(() => {
    if (initialLoadDoneRef.current) return;
    initialLoadDoneRef.current = true;
    refetchInvoiceSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchInvoiceList(invoicePagination);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoicePagination]);

  const deleteInvoice = () => {
    deleteData(
      `${API_URL}/invoice/${openConfirmationModal?.id}`,
      () => {
        setOpenConfirmationModal({ state: false, id: null });
        refetchInvoiceList();
        queryClient.invalidateQueries(["outstandingInvoices"]);
      },
      token
    );
  };

  const getTotalGST = (item) => {
    const total = item?.invoices
      ?.filter((inv) => inv?.gst === "yes" || inv?.gst === "true" || inv?.gst === true)
      ?.reduce((sum, inv) => sum + Number(inv?.total) * 0.1, 0);
    return total || 0;
  };

  // Store print function refs for each invoice - keyed by invoiceId
  const printInvoiceFunctions = useRef({});
  const printPurchaseOrderFunctions = useRef({});

  const handlePrintInvoice = (item) => {
    const invoiceId = item?.invoiceId;
    const printFn = printInvoiceFunctions.current[invoiceId]?.current;
    if (printFn) {
      printFn();
    }
  };

  const handlePrintPurchaseOrder = (item) => {
    const invoiceId = item?.invoiceId;
    const printFn = printPurchaseOrderFunctions.current[invoiceId]?.current;
    if (printFn) {
      printFn();
    }
  };

  const refreshInvoices = () => {
    refetchInvoiceList();
    refetchInvoiceSummary();
  };

  const markProviderInvoicePaid = async (invoiceId) => {
    if (window.confirm("Are you sure you want to mark this invoice as paid?")) {
      try {
        await axios.post(`${API_URL}/provider-admissions/invoices/${invoiceId}/mark-paid`);
        toast.success("Invoice marked paid");
        refreshInvoices();
      } catch (e) {
        toast.error(getApiErrorMessage(e) || "Failed to mark invoice paid");
      }
    }
  };

  const markProviderInvoiceItemUnpaid = async (itemId, invoiceId) => {
    try {
      await axios.post(`${API_URL}/provider-admissions/invoices/items/${itemId}/mark-unpaid`);
      refetchInvoiceList();
      refetchInvoiceSummary();
      setSelectedItems((prev) => {
        const next = { ...prev };
        if (next[invoiceId]) {
          const s = new Set(next[invoiceId]);
          s.delete(itemId);
          next[invoiceId] = s;
        }
        return next;
      });
    } catch (e) {
      console.error("Failed to mark item unpaid", e);
    } finally {
      setMarkUnpaidConfirm(null);
    }
  };

  const markProviderInvoiceItemsPaid = async (itemIds, invoiceId) => {
    if (window.confirm("Are you sure you want to mark these items as paid?")) {
      try {
        await axios.post(`${API_URL}/provider-admissions/invoices/items/mark-paid-bulk`, itemIds);
        toast.success(
          itemIds.length === 1 ? "Invoice item marked paid" : `${itemIds.length} items marked paid`
        );
        if (invoiceId != null) {
          setSelectedItems((prev) => ({ ...prev, [invoiceId]: new Set() }));
        }
        refreshInvoices();
      } catch (e) {
        toast.error(getApiErrorMessage(e) || "Failed to mark invoice items paid");
      }
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "title",
        header: "Invoice Title",
        Cell: ({ row }) => (
          <span className="flex items-center gap-2 min-w-0">
            <span className="truncate">{row.original?.title || "-"}</span>
            <ModuleInvoiceSourceChip invoice={row.original} prefix="Linked: " />
          </span>
        ),
        muiTableBodyCellProps: {
          sx: { maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
        },
        muiTableHeadCellProps: {
          sx: { maxWidth: 250 },
        },
      },
      {
        accessorKey: "invoiceId",
        header: "Invoice Number",
        size: 150,
        Cell: ({ row }) => (
          <span className="font-bold"> {getInvoiceNumber(row.original) || "-"}</span>
        ),
      },
      {
        accessorKey: "invoiceTo",
        header: "Invoice to",
        size: 200,
        Cell: ({ renderedCellValue }) => (
          <Tooltip title={renderedCellValue}>
            <div className="truncate max-w-[200px]">{renderedCellValue}</div>
          </Tooltip>
        ),
      },
      {
        id: "total",
        header: "Total",
        size: 150,
        Cell: ({ row }) => {
          const item = row.original;
          return formatMoney(Number(item?.subTotal) + getTotalGST(item));
        },
      },
      {
        accessorKey: "paymentStatus",
        header: "Status",
        size: 150,
        Cell: ({ row }) => {
          const item = row.original;
          if (item?.lifecycleStatus === "DRAFT") {
            return (
              <Chip label="Draft" color="default" size="small" sx={{ fontSize: 11, height: 22 }} />
            );
          }
          if (item?.lifecycleStatus === "PENDING_ACTIVATION") {
            return (
              <Chip
                label="Pending Activation"
                color="warning"
                size="small"
                sx={{ fontSize: 11, height: 22 }}
              />
            );
          }
          const status = getPaymentStatus(item);
          const color =
            status === PAYMENT_STATUS.PAID
              ? "success"
              : status === PAYMENT_STATUS.PARTIALLY_PAID
              ? "warning"
              : "error";
          return (
            <Box className="flex flex-wrap gap-1">
              <Chip
                label={paymentStatusLabel(status)}
                color={color}
                size="small"
                sx={{ fontSize: 11, height: 22 }}
              />
              {item?.hasPendingPayments && (
                <Chip
                  label="Pending"
                  color="warning"
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: 11, height: 22 }}
                />
              )}
            </Box>
          );
        },
      },
      {
        accessorKey: "issueDate",
        header: "Issue Date",
        size: 150,
        Cell: ({ row }) => {
          return row?.original?.issueDate ? <DisplayDate value={row.original?.issueDate} /> : "-";
        },
      },
      {
        id: "actions",
        header: "Actions",
        size: 72,
        enablePinning: true,
        Cell: ({ row }) => {
          const item = row.original;
          const openInvoice = (inv) => {
            inv?.status || isModuleInvoice(inv)
              ? navigate(`/admin/invoice/view/${inv?.invoiceId}`)
              : navigate(`/admin/invoice/edit/${inv?.invoiceId}`, {
                  state: { item: inv },
                });
          };
          const deleteInvoice = (inv) =>
            setOpenConfirmationModal({
              state: true,
              id: inv?.invoiceId,
            });

          return (
            <InvoiceRowActionsMenu
              item={item}
              canEmailInvoices={canEmailInvoices}
              onPrintPurchaseOrder={handlePrintPurchaseOrder}
              onPrintInvoice={handlePrintInvoice}
              onEmail={setEmailInvoice}
              onOpen={openInvoice}
              onDelete={deleteInvoice}
            />
          );
        },
      },
    ],
    [
      navigate,
      setOpenConfirmationModal,
      handlePrintInvoice,
      handlePrintPurchaseOrder,
      canEmailInvoices,
    ]
  );

  const table = useMaterialReactTable({
    columns,
    data: invoiceList?.content || [],
    rowNumberDisplayMode: "original",
    enableRowNumbers: true,
    initialState: {
      enableGlobalFilter: true,
      showGlobalFilter: true,
      density: "compact",
      columnPinning: {
        left: ["mrt-row-expand", "mrt-row-numbers", "title"],
        right: ["actions"],
      },
      pagination: {
        pageSize: 20,
        pageIndex: 0,
      },
    },
    muiPaginationProps: {
      rowsPerPageOptions: [20, 50, 100, 200, 400],
      showFirstButton: !isCompact,
      showLastButton: !isCompact,
    },
    onGlobalFilterChange: (value) => {
      // MaterialReactTable fires this once on its own during global-filter setup (with
      // `undefined`), which — left unguarded — swaps invoicePagination.search from "" to
      // undefined right after the mount-effect's fetch resolves. That changes the
      // ["invoice", invoicePagination] query key to one that's never been fetched, so the
      // just-loaded data lands in a cache entry nobody is subscribed to anymore (table shows
      // "No records to display" until something calls refetchInvoiceList() again). Normalize
      // and no-op when the value hasn't actually changed to avoid the spurious re-key.
      const normalized = value || "";
      setInvoicePagination((prev) => {
        if ((prev.search || "") === normalized) return prev;
        return { ...prev, search: normalized, pageIndex: 0 };
      });
    },
    muiTableBodyRowProps: ({ row }) => ({
      id: `invoice-row-${row.original?.invoiceId}`,
      onClick: (event) => {
        const isActionClick =
          event.target.closest(".MuiIconButton-root") ||
          event.target.closest(".MuiPopover-root") ||
          event.target.closest('input[type="checkbox"]') ||
          event.target.closest("button");
        if (!isActionClick) {
          navigate(
            isModuleInvoice(row.original)
              ? `/admin/invoice/view/${row.original?.invoiceId}`
              : `/admin/invoice/edit/${row.original?.invoiceId}`,
            { state: { item: row.original } }
          );
        }
      },
      sx: {
        cursor: "pointer",
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.04)",
        },
      },
    }),
    renderDetailPanel: ({ row }) => {
      const invoice = row.original;
      const rows = invoice?.invoices || [];
      if (!rows.length) return null;
      const invoiceId = invoice.invoiceId;
      const checkedSet = selectedItems[invoiceId] || new Set();
      const unpaidRows = rows.filter((r) => !r.status);
      const allUnpaidChecked =
        unpaidRows.length > 0 && unpaidRows.every((r) => checkedSet.has(r.id));

      const toggleItem = (itemId) => {
        setSelectedItems((prev) => {
          const current = new Set(prev[invoiceId] || []);
          if (current.has(itemId)) current.delete(itemId);
          else current.add(itemId);
          return { ...prev, [invoiceId]: current };
        });
      };

      const toggleAll = () => {
        setSelectedItems((prev) => {
          const newSet = allUnpaidChecked ? new Set() : new Set(unpaidRows.map((r) => r.id));
          return { ...prev, [invoiceId]: newSet };
        });
      };

      return (
        <div
          className="bg-app-surface-muted border-t border-app-border p-3"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-2">
            <div className="text-sm font-semibold text-app-text min-w-0">
              Client / term invoice rows
              {invoice?.accountProviderName && (
                <span className="text-app-text-muted font-normal">
                  {" "}
                  - {invoice.accountProviderName}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {checkedSet.size > 0 && (
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  onClick={() => markProviderInvoiceItemsPaid([...checkedSet], invoiceId)}
                >
                  Mark {checkedSet.size} Selected Paid
                </Button>
              )}
              {invoice?.paidNote && (
                <div className="text-xs text-app-text-muted">{invoice.paidNote}</div>
              )}
            </div>
          </div>
          {(() => {
            const showClient = rows.some((r) => r.clientName);
            const showTerm = rows.some((r) => r.termNumber || r.termMonth || r.termYear);
            const showType = rows.some((r) => r.componentType);
            const showAction =
              !!invoice?.accountProviderId &&
              getPaymentStatus(invoice) !== PAYMENT_STATUS.PARTIALLY_PAID;
            return (
              <div className="overflow-x-auto">
                {getPaymentStatus(invoice) === PAYMENT_STATUS.PARTIALLY_PAID && (
                  <div className="mb-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    Partially paid {formatMoney(Number(invoice.partialPaidAmount || 0))} against the
                    overall provider invoice. Individual commission/bonus rows are not marked
                    claimed until the invoice is fully paid.
                  </div>
                )}
                {invoice?.payments?.length > 0 && (
                  <div className="mb-3 rounded border border-app-border bg-app-surface">
                    <div className="border-b border-app-border px-3 py-2 font-semibold text-app-text">
                      Payment history
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 p-3">
                      {invoice.payments.map((payment, paymentIndex) => (
                        <div
                          key={payment.id || paymentIndex}
                          className="rounded border border-app-border bg-app-surface-muted p-2"
                        >
                          <div className="font-semibold text-app-text">
                            {formatMoney(Number(payment.amount || 0))}
                          </div>
                          <div className="text-app-text-muted">
                            {formatDisplayDate(payment.paymentDate)}
                          </div>
                          <div className="text-app-text-muted">{payment.paymentMethod || "-"}</div>
                          {payment.note && (
                            <div className="text-app-text-secondary mt-1">{payment.note}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <table className="w-full text-xs border-collapse bg-app-surface">
                  <thead>
                    <tr className="bg-app-surface-muted text-left">
                      {showAction && (
                        <th className="border px-2 py-1 w-6">
                          {unpaidRows.length > 0 && (
                            <input
                              type="checkbox"
                              checked={allUnpaidChecked}
                              onChange={toggleAll}
                              title="Select all unpaid"
                            />
                          )}
                        </th>
                      )}
                      {showClient && <th className="border px-2 py-1">Client</th>}
                      {showTerm && <th className="border px-2 py-1">Term</th>}
                      {showType && <th className="border px-2 py-1">Type</th>}
                      <th className="border px-2 py-1">Description</th>
                      <th className="border px-2 py-1">Amount</th>
                      <th className="border px-2 py-1">Status</th>
                      {showAction && <th className="border px-2 py-1">Action</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((item) => (
                      <tr key={item.id} className={checkedSet.has(item.id) ? "bg-green-50" : ""}>
                        {showAction && (
                          <td className="border px-2 py-1 w-6">
                            {!item.status && (
                              <input
                                type="checkbox"
                                checked={checkedSet.has(item.id)}
                                onChange={() => toggleItem(item.id)}
                              />
                            )}
                          </td>
                        )}
                        {showClient && (
                          <td className="border px-2 py-1">{item.clientName || "-"}</td>
                        )}
                        {showTerm && (
                          <td className="border px-2 py-1">
                            {item.termNumber ? `T${item.termNumber}` : "-"}
                            {item.termMonth || item.termYear
                              ? ` (${[item.termMonth, item.termYear].filter(Boolean).join("/")})`
                              : ""}
                          </td>
                        )}
                        {showType && (
                          <td className="border px-2 py-1">
                            {item.componentType ? (
                              <Chip
                                label={item.componentType === "COMMISSION" ? "Commission" : "Bonus"}
                                color={
                                  item.componentType === "COMMISSION" ? "primary" : "secondary"
                                }
                                size="small"
                                sx={{ fontSize: 11, height: 20 }}
                              />
                            ) : (
                              "-"
                            )}
                          </td>
                        )}
                        <td className="border px-2 py-1">{item.description || "-"}</td>
                        <td className="border px-2 py-1">
                          {formatMoney(Number(item.total || item.price || 0))}
                        </td>
                        <td className="border px-2 py-1">
                          {item.status ? (
                            <Tooltip title={item.paidNote || "Paid"}>
                              <Chip
                                label="Paid"
                                color="success"
                                size="small"
                                sx={{ fontSize: 11, height: 20 }}
                              />
                            </Tooltip>
                          ) : (
                            <Chip label="Unpaid" size="small" sx={{ fontSize: 11, height: 20 }} />
                          )}
                        </td>
                        {showAction && (
                          <td className="border px-2 py-1">
                            {!item.status ? (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markProviderInvoiceItemsPaid([item.id], invoiceId);
                                }}
                              >
                                Mark Paid
                              </Button>
                            ) : (
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMarkUnpaidConfirm({
                                    itemId: item.id,
                                    invoiceId,
                                    description: item.description,
                                    componentType: item.componentType,
                                  });
                                }}
                              >
                                Mark Unpaid
                              </Button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
      );
    },

    paginationDisplayMode: "pages",
    enablePagination: true,
    enableStickyHeader: true,
    enableColumnPinning: !isCompact,
    manualPagination: true,
    manualFiltering: true,
    muiTableContainerProps: {
      sx: {
        maxHeight: isCompact ? "60vh" : "70vh",
        minHeight: isCompact ? "50vh" : "70vh",
        overflowY: "auto",
        overflowX: "auto",
      },
    },
    rowCount: invoiceList?.totalItems,
    pageCount: Math.ceil(invoiceList?.totalPages || 0),
    state: {
      isLoading: isInvoiceListLoading,
      showProgressBars: isInvoiceListLoading,
      isSaving: isInvoiceListLoading,
      globalFilter: invoicePagination.search ?? "",
      pagination: invoicePagination,
      columnPinning: {
        left: isCompact ? [] : ["mrt-row-expand", "mrt-row-numbers", "title"],
        right: isCompact ? [] : ["actions"],
      },
    },
    onPaginationChange: (updater) => {
      setInvoicePagination((prev) => {
        const next =
          typeof updater === "function"
            ? updater({ pageIndex: prev.pageIndex, pageSize: prev.pageSize })
            : updater;
        return { ...prev, pageIndex: next.pageIndex, pageSize: next.pageSize };
      });
    },
  });

  useEffect(() => {
    const targetId = locationState?.expandInvoiceId;
    if (!targetId || !invoiceList?.content?.length) return;
    const idx = invoiceList.content.findIndex((inv) => String(inv.invoiceId) === String(targetId));
    if (idx !== -1) {
      table.setExpanded({ [String(idx)]: true });
      setTimeout(() => {
        document
          .getElementById(`invoice-row-${targetId}`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 300);
    }
  }, [locationState?.expandInvoiceId, invoiceList]);

  const headerSecondaryActions = [
    canVerifyInvoices && {
      key: "verification",
      label: "Pending Verification",
      count: pendingVerificationCount,
      onClick: () => {
        refreshPendingVerificationQueries(queryClient);
        setVerificationDialogOpen(true);
      },
    },
    canVerifyInvoices && {
      key: "activation",
      label: "Pending Activation",
      count: pendingActivationCount,
      onClick: () => {
        refreshPendingVerificationQueries(queryClient);
        setActivationDialogOpen(true);
      },
    },
    {
      key: "payslip",
      label: "Make Salary Payslip",
      onClick: () => navigate("/admin/invoice/salary-payslip"),
    },
  ].filter(Boolean);

  return (
    <div className="flex flex-wrap mt-2 sm:mt-4 dashBody">
      <div className="w-full mb-12 px-2 sm:px-4">
        <div
          className={
            "relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded " +
            "bg-app-surface text-app-text"
          }
        >
          <div className="rounded-t mb-0 px-3 sm:px-4 py-3 border-0">
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <h3 className="font-semibold text-base sm:text-lg text-app-text m-0 truncate">
                    Invoices ({invoiceList?.totalItems ?? 0})
                  </h3>
                  <RefreshData
                    refreshData={() => {
                      refetchInvoiceList();
                      refetchInvoiceSummary();
                    }}
                    loading={isInvoiceListLoading}
                    className="text-app-text-secondary shrink-0"
                  />
                </div>

                <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-end">
                  <div className="hidden sm:block">
                    <InvoicePrintThemeToggle />
                  </div>
                  <div className="hidden lg:flex items-center gap-2">
                    {headerSecondaryActions.map((action) =>
                      action.count != null ? (
                        <Badge key={action.key} badgeContent={action.count} color="warning">
                          <Button variant="outlined" size="small" onClick={action.onClick}>
                            {action.label}
                          </Button>
                        </Badge>
                      ) : (
                        <Button
                          key={action.key}
                          variant="outlined"
                          size="small"
                          startIcon={action.key === "payslip" ? <FaMoneyBillWave /> : undefined}
                          onClick={action.onClick}
                        >
                          {action.label}
                        </Button>
                      )
                    )}
                  </div>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<FaPlusCircle />}
                    component={Link}
                    to="/admin/invoice/add"
                    aria-label="Add invoice"
                  >
                    <span className="hidden sm:inline">Add Invoice</span>
                    <span className="sm:hidden">Add</span>
                  </Button>
                  {(isCompact || headerSecondaryActions.length > 0) && isCompact && (
                    <>
                      <Tooltip title="More actions">
                        <IconButton
                          size="small"
                          onClick={(e) => setHeaderMoreAnchor(e.currentTarget)}
                          aria-label="More invoice actions"
                        >
                          <MoreHoriz />
                        </IconButton>
                      </Tooltip>
                      <Menu
                        anchorEl={headerMoreAnchor}
                        open={Boolean(headerMoreAnchor)}
                        onClose={() => setHeaderMoreAnchor(null)}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                      >
                        <MenuItem
                          onClick={() => setHeaderMoreAnchor(null)}
                          disableRipple
                          sx={{ opacity: 1, cursor: "default", py: 1.5 }}
                        >
                          <InvoicePrintThemeToggle />
                        </MenuItem>
                        {headerSecondaryActions.map((action) => (
                          <MenuItem
                            key={action.key}
                            onClick={() => {
                              setHeaderMoreAnchor(null);
                              action.onClick();
                            }}
                          >
                            <ListItemText>
                              {action.label}
                              {action.count > 0 ? ` (${action.count})` : ""}
                            </ListItemText>
                          </MenuItem>
                        ))}
                      </Menu>
                    </>
                  )}
                </div>
              </div>

              <div className="w-full overflow-x-auto -mx-1 px-1">
                <ToggleButtonGroup
                  value={invoicePagination?.paymentStatus || "all"}
                  exclusive
                  onChange={(e, newValue) => {
                    if (newValue !== null) {
                      setInvoicePagination((prev) => ({
                        ...prev,
                        status: null,
                        paymentStatus: newValue === "all" ? null : newValue,
                        pageIndex: 0,
                      }));
                    }
                  }}
                  size="small"
                  sx={{
                    flexWrap: "nowrap",
                    "& .MuiToggleButton-root": {
                      border: "1px solid",
                      borderColor: "divider",
                      color: "text.secondary",
                      px: { xs: 1, sm: 1.5 },
                      fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                      whiteSpace: "nowrap",
                      "&.Mui-selected": {
                        backgroundColor: (t) => alpha(t.palette.primary.main, 0.12),
                        color: "primary.main",
                        "&:hover": {
                          backgroundColor: (t) => alpha(t.palette.primary.main, 0.18),
                        },
                      },
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    },
                  }}
                >
                  <ToggleButton value="all">All</ToggleButton>
                  <ToggleButton value={PAYMENT_STATUS.PAID}>Paid</ToggleButton>
                  <ToggleButton value={PAYMENT_STATUS.PARTIALLY_PAID}>Partial</ToggleButton>
                  <ToggleButton value={PAYMENT_STATUS.UNPAID}>Unpaid</ToggleButton>
                  <ToggleButton value="DRAFT">Draft</ToggleButton>
                  <ToggleButton value="PENDING">Pending</ToggleButton>
                  <ToggleButton value="PENDING_ACTIVATION">Activation</ToggleButton>
                </ToggleButtonGroup>
              </div>
            </div>

            {/* Summary cards */}
            <div className="mt-4 grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-7 gap-2 sm:gap-3">
              {[
                {
                  label: "Total Invoices",
                  value: invoiceSummary?.totalInvoicesCount ?? 0,
                  tone: "neutral",
                },
                {
                  label: "Total Amount",
                  value: Number(invoiceSummary?.totalAmount ?? 0),
                  valueIsMoney: true,
                  tone: "neutral",
                },
                {
                  label: "Paid Invoices",
                  value: invoiceSummary?.paidInvoicesCount ?? 0,
                  tone: "paid",
                },
                {
                  label: "Partial Invoices",
                  value: invoiceSummary?.partialInvoicesCount ?? 0,
                  tone: "partial",
                },
                {
                  label: "Unpaid Invoices",
                  value: invoiceSummary?.unpaidInvoicesCount ?? 0,
                  tone: "unpaid",
                },
                {
                  label: "Paid Amount",
                  value: Number(invoiceSummary?.paidAmount ?? 0),
                  valueIsMoney: true,
                  tone: "paid",
                },
                {
                  label: "Unpaid Amount",
                  value: Number(invoiceSummary?.unpaidAmount ?? 0),
                  valueIsMoney: true,
                  tone: "unpaid",
                },
              ].map((card, idx) => {
                const tone = INVOICE_SUMMARY_TONES[card.tone] ?? INVOICE_SUMMARY_TONES.neutral;
                const containerClasses = `rounded-xl border px-3 sm:px-4 py-2.5 sm:py-3 shadow-sm ${tone.container}`;
                const labelClasses = "text-app-text-secondary";
                const valueClasses = tone.value;

                return (
                  <div key={`${card.label}-${idx}`} className={containerClasses}>
                    <div
                      className={`text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide ${labelClasses}`}
                    >
                      {card.label}
                    </div>
                    <div className={`mt-1 text-base sm:text-lg font-bold truncate ${valueClasses}`}>
                      {card.valueIsMoney ? formatMoney(card.value) : card.value}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="w-full min-w-0 overflow-hidden">
            <div className="w-full overflow-x-auto">
              <MaterialReactTable table={table} />
            </div>
          </div>

          {/* Hidden print templates for each invoice */}
          {invoiceList?.content?.map((item) => {
            const invoiceId = item?.invoiceId;
            if (!invoiceId) return null;

            // Initialize refs for this invoice if they don't exist
            if (!printInvoiceFunctions.current[invoiceId]) {
              printInvoiceFunctions.current[invoiceId] = { current: null };
            }
            if (!printPurchaseOrderFunctions.current[invoiceId]) {
              printPurchaseOrderFunctions.current[invoiceId] = { current: null };
            }

            return (
              <InvoicePrintHandler
                key={invoiceId}
                item={item}
                onPrintInvoice={printInvoiceFunctions.current[invoiceId]}
                onPrintPurchaseOrder={printPurchaseOrderFunctions.current[invoiceId]}
              />
            );
          })}
        </div>
      </div>

      {canVerifyInvoices && (
        <Dialog
          open={verificationDialogOpen}
          onClose={() => setVerificationDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>Pending Client Invoices</DialogTitle>
          <DialogContent>
            <InvoiceVerificationRequests />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setVerificationDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {canVerifyInvoices && (
        <Dialog
          open={activationDialogOpen}
          onClose={() => setActivationDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>Pending Activations</DialogTitle>
          <DialogContent>
            <InvoiceActivationRequests />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setActivationDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      <SendInvoiceEmailDialog
        open={Boolean(emailInvoice)}
        onClose={() => setEmailInvoice(null)}
        invoiceId={emailInvoice?.invoiceId}
        invoiceNumber={emailInvoice ? getInvoiceNumber(emailInvoice) : ""}
      />

      {openConfirmationModal.state && (
        <DeleteModal
          open={openConfirmationModal.state}
          item="Invoice"
          handleCancel={() => setOpenConfirmationModal({ state: false, id: null })}
          handleDelete={() => deleteInvoice()}
          warningItems={[
            "Permanently remove this invoice and all its line items — this cannot be undone.",
            "All commission and bonus claims linked to this invoice will be marked as unclaimed.",
            "If the invoice was marked as Paid, payment records for all associated line items will be cleared.",
          ]}
        />
      )}

      <Dialog
        open={!!markUnpaidConfirm}
        onClose={() => setMarkUnpaidConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberRounded color="warning" />
          Mark Item as Unpaid
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{markUnpaidConfirm?.description}</strong> will be marked as{" "}
            <strong>unpaid</strong>.
          </DialogContentText>
          {markUnpaidConfirm?.componentType && (
            <DialogContentText sx={{ mt: 1 }}>
              The linked provider account{" "}
              <strong>
                {markUnpaidConfirm.componentType === "COMMISSION" ? "commission" : "bonus"} claim
              </strong>{" "}
              for this term will be <strong>unclaimed</strong> and editable again.
            </DialogContentText>
          )}
          <DialogContentText sx={{ mt: 1, color: "text.secondary", fontSize: 13 }}>
            This action can be reversed by marking the item paid again.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkUnpaidConfirm(null)} variant="outlined" size="small">
            Cancel
          </Button>
          <Button
            onClick={() =>
              markProviderInvoiceItemUnpaid(markUnpaidConfirm.itemId, markUnpaidConfirm.invoiceId)
            }
            variant="contained"
            color="warning"
            size="small"
          >
            Confirm Mark Unpaid
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Invoice;
